import { useCallback, useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, Button, Divider, Paper, Typography } from '@mui/material';
import { Settings } from '../../../interfaces/Settings/Settings';
import { getSettingsByType, upsertSettings } from '../../../api/Settings';
import { SettingsType } from '../../../interfaces/Settings/SettingsType';
import { GenericSettingsValue } from '../../../interfaces/Settings/GenericSettingsValue';
import { getSettingsValue, getUpdatedSettings } from '../../../utils/Settings';
import { Waiting } from '../../../components/Waiting/Waiting';
import { sharedStyles } from '../../../utils/Style';
import { SettingsRow } from '../../../components/Settings/SettingsRow';
import { SettingsValueType } from '../../../interfaces/Settings/SettingsValueType';

const basketReminderKeys = [
  'basket_reminder_notification',
  'basket_reminder_notification_interval_hours',
  'basket_reminder_notification_subject',
  'basket_reminder_notification_body',
];

export const BasketReminderSettings = () => {
  const [settings, setSettings] = useState<Settings[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshSettings = () => {
    setLoading(true);
    getSettingsByType(SettingsType.MAIL_NOTIFICATION)
      .then((s) => setSettings(s))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshSettings();
  }, []);

  const handleChange = useCallback(
    (key: string, newValue: GenericSettingsValue) =>
      setSettings(
        getUpdatedSettings(
          SettingsType.MAIL_NOTIFICATION,
          key,
          settings,
          newValue,
        ),
      ),
    [settings],
  );

  const handleSave = () => {
    setLoading(true);
    upsertSettings(settings.filter((s) => basketReminderKeys.includes(s.key)))
      .then(() => {
        toast.success('Ayarlar başarıyla kaydedildi');
        refreshSettings();
      })
      .catch((err) => {
        toast.error(err.message);
        setLoading(false);
      });
  };

  return (
    <Paper
      sx={{
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        height: '95%',
      }}
    >
      <Waiting open={loading} />

      <Typography
        sx={{
          ...sharedStyles.h5,
          fontWeight: 'bold',
        }}
      >
        Sepet Hatırlatma E-Postası
      </Typography>
      <Divider />

      <SettingsRow
        name='Aktif'
        valueType={SettingsValueType.BOOLEAN}
        value={getSettingsValue(settings, 'basket_reminder_notification')}
        onChange={(newValue) => {
          handleChange('basket_reminder_notification', newValue);
        }}
      />

      <SettingsRow
        name='Sepet Hatırlatma Süresi'
        valueType={SettingsValueType.NUMBER}
        value={getSettingsValue(
          settings,
          'basket_reminder_notification_interval_hours',
        )}
        onChange={(newValue) =>
          handleChange('basket_reminder_notification_interval_hours', newValue)
        }
        minValue={1}
        endAdornment='Saat'
      />

      <SettingsRow
        name='E-Mail Başlığı'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(
          settings,
          'basket_reminder_notification_subject',
        )}
        onChange={(newValue) =>
          handleChange('basket_reminder_notification_subject', newValue)
        }
        multiline
        placeholder='Sepetiniz sizi bekliyor!'
      />

      <SettingsRow
        name='E-Mail Mesajı'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'basket_reminder_notification_body')}
        onChange={(newValue) =>
          handleChange('basket_reminder_notification_body', newValue)
        }
        multiline
        placeholder='Sepetinizde bazı ürünler var! Stoklar tükenmeden siparişinizi tamamlayın.'
      />

      <Box sx={{ display: 'flex', flexGrow: 1 }} />

      <Box display='flex' justifyContent='flex-end'>
        <Button
          variant='contained'
          onClick={handleSave}
          sx={{ ...sharedStyles.buttonText, mt: 1.5 }}
        >
          Kaydet
        </Button>
      </Box>
    </Paper>
  );
};
